import { createBillingAgreementToken, createPayment, getPaymentsMethods, initBillingAgreementSession } from '@/api/payment';
import { IOrder, ICreatePaymentSessionArgs, ICreatePaymentSessionRes, EPaymentMethod, IPaymentMethod, IGetPaymentMethodsArgs } from '@reolink-cloud/interface';
import PaymentSDK, { IPaypalCustomizedButton, PayoneerPayment, AdyenPayment, IPayoneerCallbackResponse } from '@reolink-web/payment-sdk';
import { AdyenHandler, IAdyenStyle, IPaymentMethodHandler } from './PaymentMethods';
import { config } from '@/config/reolink';

export interface IInitFailedCbs {
    orderExpiredCb: () => Promise<void> | void;
    totalFailedCb: () => Promise<void> | void;
}

export interface IMethodsStatus {
    [EPaymentMethod.PAYPAL]: {
        enabled: boolean;
        sessionId: string;
        style: { hideAutoRenew?: boolean; };
    };
    [EPaymentMethod.PAYONEER]: {
        enabled: boolean;
        sessionId: string;
        style?: { availableCountries?: string[]; };
    };
    [EPaymentMethod.ADYEN]: {
        enabled: boolean;
        sessionId: string;
        style?: IAdyenStyle;
    };
}

interface IExactConfig {
    amount: {
        value: number;
        currency: string;
    };
    countryCode: string;
    environment?: string;
    showPayButton: boolean;
    buttonType: string;
    buttonColor: string;
    buttonSizeMode: string;
    hasHolderName: boolean;
}

interface IInitPayoneerArgs {
    proceedFunction: ( args: IPayoneerCallbackResponse ) => unknown;
    beforeServerError: ( args: IPayoneerCallbackResponse ) => unknown;
}

class PayManager {

    private readonly _paymentSDKInstance: PaymentSDK;

    private readonly _sessionId: Record<EPaymentMethod, string> = {
        paypal: '',
        payoneer: '',
        adyen: '',
    };

    private readonly _enabled: Record<EPaymentMethod, boolean> = {
        paypal: false,
        payoneer: false,
        adyen: false,
    };

    private readonly _styles: Record<EPaymentMethod, IPaymentMethod['style']> = {
        paypal: {},
        payoneer:{},
        adyen: {},
    };

    private readonly _adyenHandler: IPaymentMethodHandler | null = null;

    /** paypal所需的clientId */
    private _clientId: string = '';

    /** payoneer的支付链接 */
    private _url: string = '';

    private _customerIdToken: ICreatePaymentSessionRes['customerIdToken'] = {
        token: '',
        expiresIn: 0
    };

    private _payoneerInstance: PayoneerPayment | undefined;

    private _adyenInstance: AdyenPayment | null = null;

    private _sort: string[] = [];

    private adyenDisabledTypes: string[] = [];

    public get sort(): string[] {

        return this._sort;

    }

    public get adyenDisabledPaymentTypes(): string[] {

        return this.adyenDisabledTypes;

    }

    public get payoneerInstance(): PayoneerPayment | undefined {

        return this._payoneerInstance;

    }

    public constructor() {

        this._paymentSDKInstance = new PaymentSDK();

        this._adyenHandler = new AdyenHandler( this._paymentSDKInstance );

    }

    public get paymentsMethodsStatus(): IMethodsStatus {

        return {
            paypal: {
                enabled: this._enabled[EPaymentMethod.PAYPAL],
                sessionId: this._sessionId[EPaymentMethod.PAYPAL],
                style: this._styles[EPaymentMethod.PAYPAL]
            },
            payoneer: {
                enabled: this._enabled[EPaymentMethod.PAYONEER],
                sessionId: this._sessionId[EPaymentMethod.PAYONEER],
                style: this._styles[EPaymentMethod.PAYONEER]
            },
            adyen: {
                enabled: this._enabled[EPaymentMethod.ADYEN],
                sessionId: this._sessionId[EPaymentMethod.ADYEN],
                style: this._styles[EPaymentMethod.ADYEN] as unknown as IAdyenStyle,
            }
        };

    }

    /** 获取支付方式的状态 */
    public async getPaymentsMethodsStatus( args?: IGetPaymentMethodsArgs ): Promise<IMethodsStatus> {

        const { items, rel } = await getPaymentsMethods( args );

        for ( const method of items ) {

            this._enabled[method.gateway] = method.status === 'enable';
            this._sessionId[method.gateway] = '';
            this._styles[method.gateway] = method.style ?? {};

        }

        this._sort = rel.sort ?? [];

        return this.paymentsMethodsStatus;

    }

    /** 创建支付会话 */
    private async _createPaymentSession( args: ICreatePaymentSessionArgs ): Promise<void> {

        try {

            const { sessionId, clientId, customerIdToken, url } = await createPayment( args );
            this._sessionId[args.paymentMethod] = sessionId;

            if ( args.paymentMethod === 'paypal' ) {

                this._clientId = clientId ?? '';
                if ( customerIdToken ) {

                    this._customerIdToken = customerIdToken;

                }

            } else if ( args.paymentMethod === EPaymentMethod.PAYONEER ) {

                this._url = url;

            }

        } catch ( err ) {

            /**
             * 不渲染失败的支付方式
             */
            this._enabled[args.paymentMethod] = false;
            console.error( 'Error Create Payment Session', err );
            throw err;

        }

    }

    /** 同时创建多个支付会话 */
    private async _loadSessionsParallel( args: Omit<ICreatePaymentSessionArgs, 'paymentMethod'>, failedCbs: IInitFailedCbs ): Promise<void> {

        const promiseArr: Array<Promise<void>> = [];
        for ( const [key, val] of Object.entries( this._enabled )) {

            if ( val ) {

                if ( key === EPaymentMethod.ADYEN ) {

                    // 跳过adyen, 需要单独处理
                    continue;

                }

                promiseArr.push( this._createPaymentSession({ ...args, paymentMethod: key as EPaymentMethod }));

            }

        }

        const result = await Promise.allSettled( promiseArr );
        for ( const r of result ) {

            if ( r.status === 'rejected' ) {

                const err = r.reason;

                const symbol = err?.response?.data?.error?.symbol;

                if ( symbol === 'order_expired' ) {

                    await failedCbs.orderExpiredCb();
                    return;

                }
                console.error( err );

            }

        }
        if ( result.every(( r ) => r.status === 'rejected' ) && result.length > 0 ) {

            await failedCbs.totalFailedCb();

        }

    }

    private _initSDKInstance(): void {

        this._paymentSDKInstance.init({
            configuration: {
                sort: ['paypal'],
                ...this.paymentsMethodsStatus,
                adyen: {
                    'clientKey': this._styles.adyen?.clientKey as string ?? '',
                    'enabled': this._enabled.adyen,
                    'region': this._styles.adyen?.region as string ?? 'test',
                    'paymentMethods': this.paymentsMethodsStatus.adyen.style?.paymentMethods ?? [],
                }
            }
        });

    }

    /**
     * 创建支付会话并返回当前支付方式的状态
     *  - 创建支付会话失败会将当前支付方式禁用，所以需要将状态返回给视图
     */
    public async initPaymentSDK2Pay( args: Omit<ICreatePaymentSessionArgs, 'paymentMethod'>, failedCbs: IInitFailedCbs ): Promise<IMethodsStatus> {

        await this._loadSessionsParallel( args, failedCbs );

        this._initSDKInstance();

        return this.paymentsMethodsStatus;

    }

    public async initPaypal2Pay( args: { buyerCountry: string;currency: string; }): Promise<void> {

        await this._paymentSDKInstance.paypal.init({
            sessionId: this._sessionId['paypal'],
            isProduction: process.env.CLOUD_ENV === 'production',
            clientId: this._clientId,
            buyerCountry: args.buyerCountry,
            currency: args.currency,
            disableFundings: this._styles['paypal']?.disableFundings ?? ['card', 'mybank'],
            enableFundings: [],
            signContract: false,
            dataUserIdToken: this._customerIdToken?.token
        });

    }

    public async initPaypal2Sign( serviceId: string, mountId: string, cb: () => void ): Promise<void> {

        await this.getPaymentsMethodsStatus();

        this._initSDKInstance();

        const { clientId, setupToken, customerIdToken } = await initBillingAgreementSession({ serviceId });

        await this._paymentSDKInstance.paypal.init({
            isProduction: process.env.CLOUD_ENV === 'production',
            clientId: clientId,
            signContract: true,
            dataUserIdToken: customerIdToken.token,
            currency: 'USD', // FIXME: paypal sdk 要求必须传入currency
            disableFundings: this._styles['paypal']?.disableFundings ?? ['card'],
            enableFundings: [],
        });

        await this.renderPaypal({
            createVaultSetupToken: () => {

                return new Promise(( resolve ) => {

                    resolve( setupToken );

                });

            },
            onSignApproveData: async ( ) => {

                await createBillingAgreementToken();
                cb();

            },
            onError: ( error ) => {

                console.error( 'Paypal. An error occurred: ', error );

            },
            style: {
                color: 'gold', // gold | blue | silver
                layout: 'vertical',
                height: 46,

            }
        }, mountId );

    }

    public async renderPaypal( args: IPaypalCustomizedButton, mountId: string ): Promise<void> {

        await this._paymentSDKInstance.paypal?.customizeButton( args ).renderTo( mountId );

    }

    public createPayoneerInstance(  ): void {

        this._payoneerInstance = this._paymentSDKInstance.createPayoneer({
            cssLink: '/op-payment-widget-v3.css',
            cssIntegrity: 'sha256-JDGvkHUXjePydrxTdrGxgZ5p9es1WnNPcCgeRRxDXA4',
            sdkLink: '/op-payment-widget-v3.min.js',
            sdkIntegrity: 'sha256-w9yTQvH448OZpuK6TbpAqB2HDXwandRcC45vIBGWwpc',

        });

    }

    public async initPayoneerInstance( args: IInitPayoneerArgs ): Promise<void> {

        await this.payoneerInstance?.init();
        this.payoneerInstance?.render( 'payoneer-container', {
            listUrl: this._url,
            paymentNetworksId: 'paymentNetworksId',
            payButtonContainer: 'submitBtnContainerId',
            payButton: 'submitBtnId',
            fullPageLoading: false,
            widgetCssUrl: `${location.origin}/widget.min.css`,
            ...args
        });

    }

    public async createAdyenInstance( order: IOrder, handleSuccess: ( sessionId: string ) => unknown, onOrderExpired?: () => void ): Promise<void> {

        await this._adyenHandler?.init({
            'orderId': order.id,
            'cfg': this.paymentsMethodsStatus.adyen.style!,
            handleSuccess,
            onOrderExpired,
            orderCountry: order.payer.country
        });

        this._adyenInstance = this._adyenHandler?.getInstance() ?? null;
        this.adyenDisabledTypes = [];

        for ( const method of this.paymentsMethodsStatus['adyen'].style?.paymentMethods ?? [] ) {

            this._adyenInstance?.create( method.type, `#reo-adyen-${method.type}`, {
                'amount': {
                    'currency': order.currency,
                    'value': Math.round( Number( order.amount ) * 100 ), // FIXME: INT value
                },
                'buttonColor': 'black',
                'buttonSizeMode': 'fill',
                'buttonType': 'buy',
                'countryCode': order.payer.country || 'US',
                'environment': config.isProduction ? 'live' : 'test',
                'showPayButton': true,
                hasHolderName: true,
            } satisfies IExactConfig ).catch(( error ) => {

                this.adyenDisabledPaymentTypes.push( method.type );
                console.error( `Adyen create ${method.type} error: `, error );

            });

        }

    }

    public cleanup(): void {

        this._payoneerInstance = undefined;
        delete this._paymentSDKInstance['_payoneerInstance'];
        delete this._paymentSDKInstance['_adyenInstance'];

    }

}

export const payManager = new PayManager();
